import { BaseCanvasPack } from "./BaseCanvasPack";

export default class DefaultCanvasPack extends BaseCanvasPack {
  /**
   * Draws the canvas background matching the vivid global app background (src/index.css)
   */
  drawBackground(
    context: CanvasRenderingContext2D,
    width: number,
    height: number,
    frameTime?: number,
    level?: number
  ): void {
    // 1. Base Linear Gradient (matching body background-image linear-gradient)
    // 135deg is roughly top-left to bottom-right
    const baseGradient = context.createLinearGradient(0, 0, width, height);
    baseGradient.addColorStop(0, "#0A0E27");
    baseGradient.addColorStop(0.25, "#1a1a3e");
    baseGradient.addColorStop(0.5, "#2a1a4e");
    baseGradient.addColorStop(0.75, "#1a2a4e");
    baseGradient.addColorStop(1, "#0A0E27");
    
    context.fillStyle = baseGradient;
    context.fillRect(0, 0, width, height);

    // 2. Vivid Radial Gradients (matching body background-image radial-gradients)
    // Note: CSS lists top-to-bottom, so we draw bottom-to-top (reverse order) to layer correctly
    // or just draw them all since they are additive/transparent.
    // We'll use a large radius relative to the canvas size to simulate the "ambient light" feel.
    
    const size = Math.max(width, height);
    const radius = size * 0.6; // Slightly smaller than before to allow more distinct color spots

    const gradients = [
      // Red (Bottom layer in CSS list, draw first)
      { x: 0.8, y: 0.8, color: "rgba(255, 0, 0, 0.2)", stop: 0.05 },
      // Blue
      { x: 0.2, y: 0.7, color: "rgba(40, 40, 255, 0.25)", stop: 0.08 },
      // Green
      { x: 0.7, y: 0.5, color: "rgba(0, 255, 0, 0.25)", stop: 0.08 },
      // Yellow
      { x: 0.3, y: 0.6, color: "rgba(255, 255, 0, 0.3)", stop: 0.1 },
      // Magenta
      { x: 0.5, y: 0.8, color: "rgba(255, 0, 255, 0.3)", stop: 0.1 },
      // Cyan
      { x: 0.9, y: 0.3, color: "rgba(0, 225, 255, 0.35)", stop: 0.1 },
      // Orange (Top layer in CSS list, draw last)
      { x: 0.1, y: 0.2, color: "rgba(255, 125, 0, 0.35)", stop: 0.1 }
    ];

    gradients.forEach(g => {
      const grad = context.createRadialGradient(
        width * g.x, height * g.y, 0,
        width * g.x, height * g.y, radius
      );
      grad.addColorStop(0, g.color);
      grad.addColorStop(0.3, g.color.replace(/[\d.]+\)$/, g.stop + ")")); // Hack to lower opacity for the middle stop
      grad.addColorStop(0.6, "transparent");
      
      context.fillStyle = grad;
      context.fillRect(0, 0, width, height);
    });
  }

  /**
   * Draws the canvas frame matching current CanvasView.drawFrame() implementation
   */
  drawFrame(
    context: CanvasRenderingContext2D,
    width: number,
    height: number,
    frameTime?: number
  ): void {
    context.fillStyle = "rgba(0,0,0,0.2)";
    context.fillRect(0, 0, width, height);
    context.strokeStyle = "#FFF";
    context.lineWidth = 1;
    // Draw border: blocks are offset by 1px, so border should be:
    // - Left/Top: at 0.5 (stroke centered, visible from 0 to 1)
    // - Right: should end at width-0.5 (so blocks end at width-1, border at width-0.5 to width)
    // - Bottom: should end at height-0.5 (so blocks end at height-1, border at height-0.5 to height)
    context.strokeRect(0.5, 0.5, width - 1, height - 1);
  }
}
